import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {MedicalReportFieldsSchema} from '@/features/medical-reports/formSchemas'
import {PlannedAccessesSingleAccessFields} from '@/features/planned-accesses/components/accesses-form/planned-accesses-single-access-fields/PlannedAccessesSingleAccessFields'
import {PlannedAccessesTotalCard} from '@/features/planned-accesses/components/planned-accesses-total-card/PlannedAccessesTotalCard'
import {useGetPlannedAccesses} from '@/features/planned-accesses/queries/useGetPlannedAccesses'
import {ProjectPlannedAccess} from '@/features/planned-accesses/types'
import {MedicalReportProject} from '@/features/medical-reports/types'
import {useTranslation} from '@/translations/i18n'
import {useCallback, useEffect} from 'react'
import {FormProvider, useForm, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const MedicalReportSingleAccessesFieldsProps = MedicalReportFieldsSchema.pick({
    plannedAccesses: true
})
type MedicalReportSingleAccessesFieldsProps = z.infer<typeof MedicalReportSingleAccessesFieldsProps>

const NestedSingleAccessFormSchema = z.object({
    singleAccesses: MedicalReportFieldsSchema.shape.plannedAccesses.shape.singleAccesses
})
type NestedSingleAccessFormData = z.infer<typeof NestedSingleAccessFormSchema>

export const MedicalReportSingleAccessesFields = ({project}: {project: MedicalReportProject}) => {
    const {t} = useTranslation()
    const {control, setValue} = useFormContext<MedicalReportSingleAccessesFieldsProps>()

    const {data, isSuccess} = useGetPlannedAccesses({
        urlParams: {
            projectId: project.id
        }
    })

    const singleAccesses = useWatch({control, name: 'plannedAccesses.singleAccesses'})
    const totalSingleAccesses = singleAccesses?.reduce((acc, curr) => {
        return acc + (curr.numberOfAccesses || 0)
    }, 0)

    const handleSetSingleAccesses = useCallback(
        (data: ProjectPlannedAccess) => {
            const singleAccesses = data.plannedAccesses
                .filter(access => access.accessType.groupName === 'in_person_accesses')
                .map(access => ({
                    accessType: access.accessType,
                    numberOfAccesses: access.numberOfAccesses
                }))

            if (singleAccesses) {
                setValue('plannedAccesses.singleAccesses', singleAccesses, {shouldDirty: true})
            }
        },
        [setValue]
    )

    useEffect(() => {
        if (data && isSuccess) {
            handleSetSingleAccesses(data)
        }
    }, [data, handleSetSingleAccesses, isSuccess])

    return (
        <Flexbox direction="column" gap={6}>
            <PlannedAccessesTotalCard title={t('planned_accesses:total_single_accesses')} total={totalSingleAccesses} />
            {singleAccesses && singleAccesses.length > 0 && <MedicalReportSingleAccessesWrapperFields />}
        </Flexbox>
    )
}

const MedicalReportSingleAccessesWrapperFields = () => {
    const parentForm = useFormContext<MedicalReportSingleAccessesFieldsProps>()

    const nestedForm = useForm<NestedSingleAccessFormData>({
        defaultValues: {
            singleAccesses: parentForm.watch('plannedAccesses.singleAccesses')
        }
    })

    // Watch the nested form data and sync with parent
    const nestedSingleAccesses = useWatch({
        control: nestedForm.control,
        name: 'singleAccesses'
    })

    useEffect(() => {
        parentForm.setValue('plannedAccesses.singleAccesses', nestedSingleAccesses, {
            shouldDirty: true,
            shouldValidate: true
        })
    }, [nestedSingleAccesses])

    return (
        <FormProvider {...nestedForm}>
            <PlannedAccessesSingleAccessFields />
        </FormProvider>
    )
}
